/**
 * Contrats vNext (Commercial, Multilogements) — docs/commercial/contrats-vnext.md.
 * Construit à côté du résidentiel actuel, qui ne lit jamais ce module.
 */
import type { ContractListFilters, ContractStatus, ContractStatusV2 } from '@/features/contracts'

export type VnextContractKind = 'COMMERCIAL' | 'MULTIFAMILY'

/** Parcours d'un Contrat : LEGACY = assistant et fiche actuels, VNEXT = nouveau système. */
export type ContractFlow = 'LEGACY' | 'VNEXT'

export type LocationPricingMode = 'GLOBAL' | 'PER_LOCATION'

export const vnextKindLabel: Record<VnextContractKind, string> = {
  COMMERCIAL: 'Commercial',
  MULTIFAMILY: 'Multilogements',
}

/** Segment d'URL de la liste et de l'assistant (`/contracts/commercial`, `/contracts/multilogements`). */
export const vnextKindSlug: Record<VnextContractKind, string> = {
  COMMERCIAL: 'commercial',
  MULTIFAMILY: 'multilogements',
}

export function vnextKindFromSlug(slug: string | undefined): VnextContractKind | undefined {
  return (Object.keys(vnextKindSlug) as VnextContractKind[]).find((kind) => vnextKindSlug[kind] === slug)
}

/**
 * Filtres de la liste : mêmes vues rapides et mêmes statuts affichés que la liste Résidentiel
 * (demande du propriétaire, 26 oct. 2026), même dérivation SQL.
 */
export interface VnextListScope {
  kind: VnextContractKind
  search: string
  displayStatus?: NonNullable<ContractListFilters['displayStatus']>
  /** VIP : sans facturation et actif, même règle que la liste Résidentiel. */
  vip?: boolean
}

export interface VnextListFilters extends VnextListScope {
  page: number
  pageSize: number
}

export interface VnextLocationSummary {
  total: number
  ready: number
  assigned: number
}

export interface VnextContractListItem {
  id: string
  number: string
  flow: ContractFlow
  /** Entreprise facturée, à défaut le nom du Client. */
  company: string
  /** Responsable par défaut du Contrat, à défaut le Client. */
  contact?: string
  season?: string
  priceCents?: number
  status: ContractStatus
  statusV2?: ContractStatusV2
  signedOn?: string
  sentAt?: string
  openedSinceLastSend?: boolean
  sansFacturation?: boolean
  /** Absent pour un Contrat LEGACY : ses adresses ne portent pas les statuts vNext. */
  locations?: VnextLocationSummary
}

export interface VnextContractPage {
  items: VnextContractListItem[]
  total: number
  source: 'supabase' | 'fixtures'
}

/** « 22 prêtes sur 30 » : part prête, bornée à [0, 1]. */
export function readyShare(summary: VnextLocationSummary): number {
  if (summary.total <= 0) return 0
  return Math.min(1, Math.max(0, summary.ready / summary.total))
}

export interface VnextContact {
  firstName?: string
  lastName?: string
  phone?: string
  phoneSecondary?: string
  email?: string
  roleLabel?: string
  isEmergency?: boolean
}

export type AddressValidationStatus = 'VALID' | 'NEEDS_CONFIRMATION'

export interface VnextLocationInput {
  line1: string
  line2?: string
  city?: string
  province?: string
  postalCode?: string
  formattedAddress?: string
  latitude?: number
  longitude?: number
  validationStatus: AddressValidationStatus
  siteName?: string
  siteType?: string
  unitCount?: number
  parkingSpaces?: number
  entryCount?: number
  /** Obligatoire en prix par adresse, interdit en prix global (une seule source de vérité). */
  priceCents?: number
  /** Services de l'adresse. Absent = ceux du Contrat (`create_vnext_contract_v1`). */
  services?: string[]
  /** Absent = responsable propre fourni ou non : la RPC hérite si aucun `contact`. */
  contact?: VnextContact
}

export interface CreateVnextContractCommand {
  idempotencyKey: string
  contractType: VnextContractKind
  clientId: string
  season: string
  billing: {
    name?: string
    line1: string
    line2?: string
    city?: string
    province?: string
    postalCode?: string
    country?: string
  }
  defaultContact?: VnextContact
  pricingMode: LocationPricingMode
  globalPriceCents?: number
  services: string[]
  locations: VnextLocationInput[]
}

export interface CreateVnextContractResult {
  contractId: string
  number: string
  totalCents: number
  locationCount: number
}

export type OperationalStatus = 'NOT_STARTED' | 'INCOMPLETE' | 'COMPLETE'

export interface VnextLocationReadiness {
  position: boolean
  contact: boolean
  entry: boolean
  snowClearingZone: boolean
  status: OperationalStatus
}

export interface VnextServiceLocation {
  id: string
  order: number
  formattedAddress: string
  line1: string
  city?: string
  postalCode?: string
  latitude?: number
  longitude?: number
  siteName?: string
  siteType?: string
  unitCount?: number
  parkingSpaces?: number
  priceCents?: number
  validationStatus?: AddressValidationStatus
  useContractDefaultContact: boolean
  contact?: VnextContact & { source: 'CONTRACT' | 'LOCATION' }
  route?: { id: string; name: string; color: string }
  readiness: VnextLocationReadiness
}

export interface VnextLocationsOverview {
  contractId: string
  contractType: 'RESIDENTIAL' | 'COMMERCIAL' | 'MULTIFAMILY'
  pricingMode?: LocationPricingMode
  locations: VnextServiceLocation[]
  totals: { total: number; ready: number; toConfigure: number; assigned: number; unassigned: number }
}

/** Montant du Contrat : prix global, ou somme des prix par adresse (même règle que la RPC). */
export function contractTotalCents(
  pricingMode: LocationPricingMode,
  globalPriceCents: number | undefined,
  locations: readonly Pick<VnextLocationInput, 'priceCents'>[],
): number {
  if (pricingMode === 'GLOBAL') return globalPriceCents ?? 0
  return locations.reduce((sum, location) => sum + (location.priceCents ?? 0), 0)
}

export interface ContractFlowRef {
  id: string
  number: string
  flow: ContractFlow
}

export const operationalStatusLabel: Record<OperationalStatus, string> = {
  NOT_STARTED: 'À configurer',
  INCOMPLETE: 'Incomplète',
  COMPLETE: 'Prête',
}

/** Première chose qui empêche l'adresse d'être prête, dans l'ordre de configuration. */
export function locationTodo(
  location: Pick<VnextServiceLocation, 'readiness' | 'validationStatus'>,
): string | undefined {
  if (location.validationStatus !== 'VALID') return 'Adresse à confirmer'
  if (!location.readiness.position) return 'Position à placer'
  if (!location.readiness.contact) return 'Responsable sans téléphone'
  if (!location.readiness.entry) return 'Aucun point d’entrée'
  if (!location.readiness.snowClearingZone) return 'Zone à déneiger à tracer'
  return undefined
}

export type LocationContactRole = 'PRIMARY' | 'EMERGENCY' | 'MANAGER' | 'CARETAKER' | 'OTHER'

export interface LocationContact extends VnextContact {
  role: LocationContactRole
}

export type EntryType = 'MAIN' | 'SECONDARY' | 'LOADING_DOCK' | 'GARAGE' | 'EMPLOYEE' | 'OTHER'

export interface LocationEntry {
  label: string
  entryType: EntryType
  latitude: number
  longitude: number
  detectionRadiusM: number
  enabled: boolean
  isPrimary: boolean
}

export type ZoneKind = 'SNOW_CLEARING' | 'SNOW_STORAGE' | 'FORBIDDEN' | 'PRIORITY'

export interface PolygonGeometry {
  type: 'Polygon'
  coordinates: [number, number][][]
}

export interface LocationZone {
  label: string
  kind: ZoneKind
  geometry: PolygonGeometry
  surfaceM2?: number
}

export const zoneKindLabel: Record<ZoneKind, string> = {
  SNOW_CLEARING: 'Zone à déneiger',
  SNOW_STORAGE: 'Dépôt de neige',
  FORBIDDEN: 'Zone interdite',
  PRIORITY: 'Zone prioritaire',
}

export const entryTypeLabel: Record<EntryType, string> = {
  MAIN: 'Entrée principale',
  SECONDARY: 'Entrée secondaire',
  LOADING_DOCK: 'Quai de livraison',
  GARAGE: 'Accès garage',
  EMPLOYEE: 'Entrée employés',
  OTHER: 'Autre accès',
}

/** Profil opérationnel : champs libres propres à chaque type (liste blanche côté serveur). */
export type LocationProfile = Record<string, string | number | boolean>

export interface ServiceLocationDetail {
  id: string
  contractId: string
  contractNumber: string
  contractType: VnextContractKind
  contractLocked: boolean
  position: number
  total: number
  previousId?: string
  nextId?: string
  line1: string
  city?: string
  province?: string
  postalCode?: string
  formattedAddress: string
  latitude?: number
  longitude?: number
  validationStatus?: AddressValidationStatus
  siteName?: string
  siteType?: string
  unitCount?: number
  parkingSpaces?: number
  entryCount?: number
  priceCents?: number
  profile: LocationProfile
  useContractDefaultContact: boolean
  contacts: LocationContact[]
  entries: LocationEntry[]
  legacyFlag?: { latitude: number; longitude: number }
  zones: LocationZone[]
  route?: { id: string; name: string; color: string }
  readiness: VnextLocationReadiness
}

export interface UpdateServiceLocationCommand {
  locationId: string
  siteName?: string
  siteType?: string
  unitCount?: number | null
  parkingSpaces?: number | null
  entryCount?: number | null
  profile?: LocationProfile
  useContractDefaultContact?: boolean
  contacts?: LocationContact[]
  /** Responsable propre seul (action de masse) : les autres contacts restent. */
  primaryContact?: VnextContact
  address?: {
    line1: string
    city?: string
    province?: string
    postalCode?: string
    formattedAddress?: string
    latitude: number
    longitude: number
  }
}

/** Champs copiables d'une adresse à l'autre. Jamais : adresse, coordonnées, entrées, zones. */
export const copyableFields: Record<VnextContractKind, readonly { key: string; label: string }[]> = {
  MULTIFAMILY: [
    { key: 'importantHours', label: 'Heures importantes' },
    { key: 'vehicleMoves', label: 'Déplacement des véhicules' },
    { key: 'municipalEdging', label: 'Bordage municipal' },
    { key: 'tenantAccess', label: 'Accès locataires' },
    { key: 'garages', label: 'Garages' },
    { key: 'notes', label: 'Notes opérationnelles' },
  ],
  COMMERCIAL: [
    { key: 'siteType', label: 'Type d’établissement' },
    { key: 'openingHours', label: 'Heures d’ouverture' },
    { key: 'clearBefore', label: 'Entrées libres avant' },
    { key: 'weekendHours', label: 'Fin de semaine' },
    { key: 'priorityAccess', label: 'Accès prioritaires' },
    { key: 'loadingDocks', label: 'Quais de livraison' },
    { key: 'employeeEntrances', label: 'Entrées employés' },
    { key: 'constraints', label: 'Contraintes' },
    { key: 'notes', label: 'Notes opérationnelles' },
  ],
}

export type ConversionType = 'TO_COMMERCIAL' | 'TO_MULTIFAMILY'

export const conversionKind: Record<ConversionType, VnextContractKind> = {
  TO_COMMERCIAL: 'COMMERCIAL',
  TO_MULTIFAMILY: 'MULTIFAMILY',
}

/** Sort du Contrat source à la signature du nouveau : annulé (même saison) ou conservé. */
export interface ContractConversionFinalization {
  outcome: 'SOURCE_CANCELLED' | 'SOURCE_KEPT'
  finalizedAt: string
  cancelledInvoices: number
  transferredCents: number
  appliedCents: number
}

export interface ContractConversionLink {
  number: string
  clientId: string
  completedAt: string
  finalization?: ContractConversionFinalization
  /** Échec de la finalisation à la signature : elle se relance depuis la fiche. */
  finalizationError?: string
}

export interface ContractConversionState {
  draft?: { conversionId: string; conversionType: ConversionType; startedAt: string }
  convertedTo?: ContractConversionLink
  convertedFrom?: ContractConversionLink
}
